import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../api/api";
import {
  setVoiceAdd,
  voiceAddInitialState,
} from "../../redux/reducers/voiceAdd/voiceAddReducer";
import { annotateDrafts } from "./annotateDrafts";
import { confirmDraft } from "./confirmDraft";
import { cancelPendingDraftForm, openDraftForm } from "./openForm";
import { buildVoiceCatalog } from "./buildVoiceCatalog";
import { setMembershipCardsScannerPopupOpen } from "../../redux/actions/membershipCard/membershipCardAction";
import { getDefaultPhoneCountry } from "../../utils/phone";
import {
  applyCreatedDependencies,
  dependencyOrder,
  pendingDependencies,
} from "./orchestration";
import { attachExistingMember, memberRecord } from "./attachExistingMember";
import { MemberSearch } from "./memberSearch";
import {
  endVoiceAddSession,
  trackVoiceAddConfirm,
  trackVoiceAddEvent,
} from "./trackSession";
import { isVoiceFabRaised } from "./VoiceAddFab";
import "./voiceAdd.scss";

const PANEL_SIZE_KEY = "voiceAddPanelSize.v4";
const PANEL_MIN_WIDTH = 480;
const PANEL_MIN_HEIGHT = 420;
const PANEL_GUTTER = 16;
const PANEL_MIC_SLOT = 92;
const PANEL_DEFAULT_WIDTH = 960;
const FAB_SIZE = 53;
const FAB_RIGHT = 10;
const FAB_BOTTOM = 40;
const FAB_BOTTOM_RAISED = 108;

const viewportSize = () => ({
  width:
    (typeof document !== "undefined" &&
      document.documentElement &&
      document.documentElement.clientWidth) ||
    window.innerWidth,
  height:
    (typeof document !== "undefined" &&
      document.documentElement &&
      document.documentElement.clientHeight) ||
    window.innerHeight,
});

const defaultPanelSize = () => {
  if (typeof window === "undefined") {
    return { width: PANEL_DEFAULT_WIDTH, height: 800 };
  }
  const vp = viewportSize();
  return {
    width: Math.min(
      PANEL_DEFAULT_WIDTH,
      Math.max(PANEL_MIN_WIDTH, vp.width - PANEL_MIC_SLOT - PANEL_GUTTER)
    ),
    height: Math.max(PANEL_MIN_HEIGHT, vp.height - PANEL_GUTTER * 2),
  };
};

const readPanelSize = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(PANEL_SIZE_KEY) || "null");
    if (
      stored &&
      Number(stored.width) >= PANEL_MIN_WIDTH &&
      Number(stored.height) >= PANEL_MIN_HEIGHT
    ) {
      return { width: Number(stored.width), height: Number(stored.height) };
    }
  } catch (error) {
    /* ignore */
  }
  return defaultPanelSize();
};

const clampPanelSize = (next) => {
  const vp = viewportSize();
  const maxWidth = Math.max(
    PANEL_MIN_WIDTH,
    vp.width - PANEL_MIC_SLOT - PANEL_GUTTER
  );
  const maxHeight = Math.max(
    PANEL_MIN_HEIGHT,
    vp.height - PANEL_GUTTER * 2
  );
  return {
    width: Math.min(maxWidth, Math.max(PANEL_MIN_WIDTH, Math.round(next.width))),
    height: Math.min(
      maxHeight,
      Math.max(PANEL_MIN_HEIGHT, Math.round(next.height))
    ),
  };
};

const micCenter = (raised, fabEl) => {
  if (fabEl && fabEl.getBoundingClientRect) {
    const rect = fabEl.getBoundingClientRect();
    if (rect.width && rect.height) {
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        left: rect.left,
      };
    }
  }
  const vp = viewportSize();
  const x = vp.width - FAB_RIGHT - FAB_SIZE / 2;
  const y =
    vp.height -
    (raised ? FAB_BOTTOM_RAISED : FAB_BOTTOM) -
    FAB_SIZE / 2;
  return { x, y, left: x - FAB_SIZE / 2 };
};

const arrowPathToMic = (size, offset, mic) => {
  const vp = viewportSize();
  const right = vp.width - PANEL_MIC_SLOT + offset.x;
  const top = PANEL_GUTTER + offset.y;
  const anchorY = top + size.height - 46;
  const half = 11;
  const startX = right - 10;
  const tipX = (mic.left || mic.x - FAB_SIZE / 2) + 8;
  const tipY = mic.y;
  const dx = Math.max(8, tipX - startX);
  const c1x = startX + dx * 0.42;
  const c2x = startX + dx * 0.84;
  return [
    `M ${startX} ${anchorY - half}`,
    `C ${c1x} ${anchorY - half}, ${c2x} ${tipY - 2}, ${tipX} ${tipY}`,
    `C ${c2x} ${tipY + 2}, ${c1x} ${anchorY + half}, ${startX} ${anchorY + half}`,
    "Z",
  ].join(" ");
};

const detectLanguage = (text, fallback) => {
  const arabic = (String(text).match(/[\u0600-\u06FF]/g) || []).length;
  const latin = (String(text).match(/[A-Za-z]/g) || []).length;
  if (arabic === 0 && latin === 0) {
    return fallback === "Arabic" ? "Arabic" : "English";
  }
  return arabic > latin ? "Arabic" : "English";
};

const workingCopy = {
  English: {
    title: "Building your request",
    detail: "Checking required details and matching existing records…",
  },
  Arabic: {
    title: "عم بجهّز طلبك",
    detail: "عم بتأكد من المعلومات المطلوبة وبطابق السجلات الموجودة…",
  },
};

const errorCopy = {
  English: "I could not read that. Try again in a moment.",
  Arabic: "ما قدرت أقرأ الطلب. جرّب مرة ثانية بعد قليل.",
};

const internalReply = /Gemini|API key|AI_IP_BLOCKED|IP address restriction|قيود المفتاح|مفتاح Gemini|عنوان الجهاز/i;

export const publicVoiceReply = (reply, language) => {
  const text = String(reply || "").trim();
  if (!text || internalReply.test(text)) {
    return errorCopy[language] || errorCopy.English;
  }
  return text;
};

const listeningCopy = {
  "en-US": "Listening in English. Keep talking. Press pause to stop.",
  "ar-SA": "عم بسمع بالعربي. كمّل حكي. اضغط إيقاف لما تخلّص.",
};

const micErrorCopy = {
  unsupported: {
    English: "Voice input works in Chrome or Edge. You can type instead.",
    Arabic: "الصوت يشتغل على Chrome أو Edge. فيك تكتب.",
  },
  "not-allowed": {
    English: "Microphone is blocked in the browser.",
    Arabic: "المايك ممنوع من المتصفح.",
  },
  "audio-capture": {
    English: "No microphone was found.",
    Arabic: "ما لقيت مايك.",
  },
  network: {
    English: "Speech service is unavailable. You can type instead.",
    Arabic: "خدمة الصوت مو متاحة. فيك تكتب.",
  },
  silent: {
    English: "I didn't hear anything. Click the mic and speak again.",
    Arabic: "ما سمعت شي. اضغط المايك واحكي مرة ثانية.",
  },
};

const MicIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.7V21h2v-3.3A7 7 0 0 0 19 11h-2z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 5h3.2v14H7zM13.8 5H17v14h-3.2z" />
  </svg>
);

const statusCopy = {
  English: {
    title: "Request details",
    subtitle: "Everything understood so far",
    needed: (count) => `${count} still needed`,
    ready: "Ready for review",
    add: "Add",
    field: "Field",
    status: "Status",
    missing: "Missing",
    saved: "Saved",
  },
  Arabic: {
    title: "تفاصيل الطلب",
    subtitle: "كل المعلومات اللي فهمتها لحد الآن",
    needed: (count) => `باقي ${count}`,
    ready: "جاهز للمراجعة",
    add: "الإضافة",
    field: "المعلومة",
    status: "الحالة",
    missing: "ناقص",
    saved: "تم حفظه",
  },
};

const StatusTable = ({ drafts, language }) => {
  const copy = statusCopy[language] || statusCopy.English;
  const rows = (drafts || []).reduce((list, draft) => {
    const scanLabels = (draft.scans || []).map((scan) => scan.label);
    (draft.missing || []).forEach((field) => {
      if (scanLabels.indexOf(field) !== -1) return;
      list.push({ add: draft.label, field, missing: true, value: copy.missing });
    });
    (draft.saved || []).forEach((item) => {
      list.push({
        add: draft.label,
        field: item.field,
        missing: false,
        value: `${copy.saved} · ${item.value}`,
      });
    });
    return list;
  }, []);
  if (!rows.length) return null;
  const saved = rows.filter((row) => !row.missing).length;
  const missing = rows.length - saved;
  return (
    <div
      className={`voice-add__table${language === "Arabic" ? " is-ar" : ""}`}
    >
      <div className="voice-add__table-title">
        <div>
          <b>{copy.title}</b>
          <span>{copy.subtitle}</span>
        </div>
        <em className={missing ? "" : "is-ready"}>
          {missing ? copy.needed(missing) : copy.ready}
        </em>
      </div>
      <div className="voice-add__row is-head">
        <span>{copy.add}</span>
        <span>{copy.field}</span>
        <span>{copy.status}</span>
      </div>
      {rows.map((row, index) => (
        <div className="voice-add__row" key={`${row.add}-${row.field}-${index}`}>
          <b>{row.add}</b>
          <span>{row.field}</span>
          <span className={row.missing ? "voice-add__missing" : "voice-add__saved"}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const ChatMessage = ({ message }) => (
  <div
    className={`voice-add__msg ${
      message.role === "user" ? "is-user" : "is-bot"
    }${message.language === "Arabic" ? " is-ar" : ""}${
      message.isCompletion ? " is-complete" : ""
    }`}
  >
    <span className="voice-add__who">
      {message.role === "user"
        ? message.language === "Arabic"
          ? "أنت"
          : "You"
        : message.language === "Arabic"
          ? "المساعد"
          : "Assistant"}
    </span>
    {message.text}
  </div>
);

const FAILED_OUTCOMES = [
  "error",
  "blocked",
  "needs_form",
  "needs_scan",
  "existing",
];

const outcomeMeta = {
  created: { label: "Created", tone: "ok" },
  existing: { label: "Already exists", tone: "bad" },
  used: { label: "Using existing", tone: "ok" },
  rejected: { label: "Rejected", tone: "muted" },
  needs_form: { label: "Needs a fix", tone: "bad" },
  needs_scan: { label: "Needs a card scan", tone: "bad" },
  error: { label: "Not created", tone: "bad" },
  blocked: { label: "Waiting", tone: "warn" },
};

const issueList = (draft, extra) => {
  const fromDraft = (draft && draft.fieldErrors) || [];
  const missing = ((draft && draft.missing) || []).map((field) => ({
    message: `${field} is missing`,
  }));
  const extraItems = extra || [];
  const seen = {};
  return fromDraft
    .concat(missing)
    .concat(extraItems)
    .map((item) => (typeof item === "string" ? { message: item } : item))
    .filter((item) => {
      const text = item && item.message;
      if (!text || seen[text]) return false;
      seen[text] = true;
      return true;
    });
};

const FieldList = ({ items, empty }) => {
  if (!items || !items.length) {
    return empty ? <p className="voice-add__muted">{empty}</p> : null;
  }
  return (
    <ul className="voice-add__fields">
      {items.map((item, index) => (
        <li key={`${item.field}-${item.value}-${index}`}>
          <span>{item.field}</span>
          <b>{item.value}</b>
        </li>
      ))}
    </ul>
  );
};

const IssueList = ({ issues, title }) => {
  if (!issues || !issues.length) return null;
  return (
    <div className="voice-add__alert" role="alert">
      {title ? <strong>{title}</strong> : null}
      <ul>
        {issues.map((issue, index) => (
          <li key={`${issue.message}-${index}`}>{issue.message}</li>
        ))}
      </ul>
    </div>
  );
};

const StatusBadge = ({ tone, children }) => (
  <span className={`voice-add__badge is-${tone || "muted"}`}>{children}</span>
);

const resultCounts = (results) => {
  const list = results || [];
  return {
    created: list.filter((item) => item.outcome === "created").length,
    existing: list.filter((item) => item.outcome === "existing").length,
    rejected: list.filter((item) => item.outcome === "rejected").length,
    attention: list.filter((item) => FAILED_OUTCOMES.includes(item.outcome))
      .length,
  };
};

const WorkOverlay = ({ title, detail }) => (
  <div className="voice-add__overlay" role="status" aria-live="assertive">
    <span className="voice-add__spinner" aria-hidden="true" />
    <b>{title}</b>
    {detail ? <p>{detail}</p> : null}
  </div>
);

const VoiceAdd = ({
  openClass,
  openWorkshop,
  openCourt,
  onStaffPermissions,
  loggedInRole,
}) => {
  const dispatch = useDispatch();
  const voiceAdd = useSelector((state) => state.voiceAdd) || voiceAddInitialState;
  const { gym, gymConfig } = useSelector((state) => state.gym);
  const { loginUser } = useSelector((state) => state.login || {});
  const { permissions, subAdminPermissions, subAdminType } = useSelector(
    (state) => state.permissions
  );
  const editModalOpen = useSelector((state) => {
    const sidebar = state.sidebar || {};
    const sidebarOpen = [
      "trainerModal",
      "userModal",
      "retailModal",
      "privateModal",
      "workshopModal",
      "courtModal",
      "appointmentModal",
      "wodModal",
      "followupProgramModal",
      "gateModal",
      "packageModal",
      "saleModal",
      "staffModal",
    ].some((key) => Boolean(sidebar[key]));
    return sidebarOpen || Boolean(state.leads && state.leads.openLeadModal);
  });
  const [listening, setListening] = useState(false);
  const [micNote, setMicNote] = useState("");
  const [speechLang, setSpeechLang] = useState("ar-SA");
  const [progress, setProgress] = useState(null);
  const [pickingDraftId, setPickingDraftId] = useState(null);
  const recognitionRef = useRef(null);
  const wantMic = useRef(false);
  const spokenRef = useRef("");
  const textRef = useRef("");
  const sendRef = useRef(null);
  const languageRef = useRef("English");
  const speechLangRef = useRef(speechLang);
  const editingRef = useRef(false);
  const editTimer = useRef(null);
  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const prevEditModalOpen = useRef(false);
  const editLaunchRef = useRef(0);
  const panelSizeRef = useRef(defaultPanelSize());
  const [panelSize, setPanelSize] = useState(() =>
    typeof window === "undefined"
      ? defaultPanelSize()
      : clampPanelSize(readPanelSize())
  );
  const [panelShown, setPanelShown] = useState(Boolean(voiceAdd.open));
  const [panelReady, setPanelReady] = useState(Boolean(voiceAdd.open));
  const [panelLeaving, setPanelLeaving] = useState(false);
  const [panelResizing, setPanelResizing] = useState(false);
  const [panelDragging, setPanelDragging] = useState(false);
  const [panelOffset, setPanelOffset] = useState({ x: 0, y: 0 });
  const panelOffsetRef = useRef({ x: 0, y: 0 });
  const {
    open,
    phase,
    step,
    messages,
    drafts,
    results,
    busy,
    readyForReview,
    language,
    text = "",
  } = voiceAdd;
  const fabRaised = isVoiceFabRaised(gym, gymConfig);

  useEffect(() => {
    if (
      voiceAdd.editingDraftId &&
      prevEditModalOpen.current &&
      !editModalOpen
    ) {
      dispatch(
        setVoiceAdd({
          phase: "ready",
          step: "review",
          prefill: null,
          editingDraftId: null,
          open: true,
        })
      );
    }
    prevEditModalOpen.current = editModalOpen;
  }, [editModalOpen, voiceAdd.editingDraftId, dispatch]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, drafts, busy, open, step]);

  useEffect(() => {
    panelSizeRef.current = panelSize;
  }, [panelSize]);

  useEffect(() => {
    const onResize = () => {
      const next = clampPanelSize(panelSizeRef.current);
      panelSizeRef.current = next;
      setPanelSize(next);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (open) {
      setPanelShown(true);
      setPanelLeaving(false);
      if (panelReady) return undefined;
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPanelReady(true));
      });
      return () => cancelAnimationFrame(frame);
    }
    panelOffsetRef.current = { x: 0, y: 0 };
    setPanelOffset({ x: 0, y: 0 });
    if (!panelShown) return undefined;
    setPanelReady(false);
    setPanelLeaving(true);
    const timer = setTimeout(() => {
      setPanelShown(false);
      setPanelLeaving(false);
    }, 360);
    return () => clearTimeout(timer);
  }, [open, panelShown, panelReady]);

  const beginPanelResize = (event, edges) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const start = panelSizeRef.current;
    setPanelResizing(true);
    const onMove = (moveEvent) => {
      const next = clampPanelSize({
        width:
          edges.indexOf("w") !== -1
            ? start.width + (startX - moveEvent.clientX)
            : start.width,
        height:
          edges.indexOf("s") !== -1
            ? start.height + (moveEvent.clientY - startY)
            : start.height,
      });
      panelSizeRef.current = next;
      setPanelSize(next);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setPanelResizing(false);
      try {
        sessionStorage.setItem(
          PANEL_SIZE_KEY,
          JSON.stringify(panelSizeRef.current)
        );
      } catch (error) {
        /* ignore */
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const beginPanelDrag = (event) => {
    if (typeof event.button === "number" && event.button !== 0) return;
    if (event.target.closest && event.target.closest(".voice-add__head-actions")) {
      return;
    }
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const start = panelOffsetRef.current;
    setPanelDragging(true);
    const onMove = (moveEvent) => {
      const next = {
        x: start.x + (moveEvent.clientX - startX),
        y: start.y + (moveEvent.clientY - startY),
      };
      panelOffsetRef.current = next;
      setPanelOffset(next);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setPanelDragging(false);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const patch = (next) => dispatch(setVoiceAdd(next));
  textRef.current = text;
  languageRef.current = language;
  speechLangRef.current = speechLang;

  const stopListening = () => {
    wantMic.current = false;
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch (error) {
        /* already stopped */
      }
    }
    setListening(false);
  };

  useEffect(() => {
    if (!open) stopListening();
  }, [open]);

  useEffect(() => () => stopListening(), []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(
      el.scrollHeight,
      Math.floor(window.innerHeight * 0.45)
    )}px`;
  }, [text, open, step]);

  const startRecognition = () => {
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Speech || !wantMic.current) return;
    const previous = recognitionRef.current;
    if (previous) {
      previous.onresult = null;
      previous.onerror = null;
      previous.onend = null;
      try {
        previous.stop();
      } catch (error) {
        /* already stopped */
      }
    }
    const recognition = new Speech();
    recognition.lang = speechLangRef.current;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      if (recognitionRef.current !== recognition || editingRef.current) return;
      let finalText = "";
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const piece = event.results[index][0].transcript;
        if (event.results[index].isFinal) finalText += piece;
        else interim += piece;
      }
      if (finalText) {
        spokenRef.current = `${spokenRef.current} ${finalText}`.trim();
      }
      patch({ text: `${spokenRef.current} ${interim}`.trim() });
    };
    recognition.onerror = (event) => {
      const code = event && event.error;
      if (code === "no-speech" || code === "aborted") return;
      wantMic.current = false;
      setListening(false);
      const copy = micErrorCopy[code] || micErrorCopy.network;
      setMicNote(copy[languageRef.current] || copy.English);
    };
    recognition.onend = () => {
      if (!wantMic.current || recognitionRef.current !== recognition) {
        return;
      }
      window.setTimeout(() => {
        if (!wantMic.current || recognitionRef.current !== recognition) return;
        try {
          recognition.start();
        } catch (error) {
          window.setTimeout(() => {
            if (!wantMic.current || recognitionRef.current !== recognition) return;
            try {
              recognition.start();
            } catch (retryError) {
              /* Chrome ended the session; the next pause click still stops it */
            }
          }, 250);
        }
      }, 100);
    };
    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch (error) {
      wantMic.current = false;
      setListening(false);
    }
  };

  const send = async (value) => {
    const message = String(value || "").trim();
    if (!message || busy) return;
    const turnLanguage = detectLanguage(message, language);
    stopListening();
    spokenRef.current = "";
    patch({
      text: "",
      busy: true,
      phase: "interpreting",
      step: "chat",
      language: turnLanguage,
      messages: messages.concat({
        role: "user",
        text: message,
        language: turnLanguage,
      }),
    });

    try {
      const login = JSON.parse(
        localStorage.getItem("connectToFitGymPanelUser") || "{}"
      );
      const role =
        login.isSuperGymAdmin && login.isSubAdmin ? "STAFF" : login.role || "";
      const res = await api(
        "voice-add/interpret",
        {
          message,
          drafts,
          history: messages
            .filter((item) => item.role === "user")
            .map((item) => item.text)
            .slice(-8),
          previousLanguage: language,
          sessionId: voiceAdd.sessionId,
          defaultCountry: getDefaultPhoneCountry(
            (gym && gym.country) || login.country
          ),
          actions: buildVoiceCatalog({
            gym,
            gymConfig,
            role,
            loggedInRole,
            permissions,
            subAdminType,
            subAdminPermissions,
            loginUser,
          }),
        },
        "post"
      );
      if (!res || res.status !== 200) {
        throw new Error("interpret failed");
      }
      const data = res.data.data || {};
      const replyLanguage = data.language || turnLanguage;
      const reply = publicVoiceReply(data.reply, replyLanguage);
      const responseDrafts = await annotateDrafts(data.drafts || [], {
        language: replyLanguage,
        message,
        country: getDefaultPhoneCountry(
          (gym && gym.country) || login.country
        ),
      });
      const draftsReady =
        responseDrafts.length > 0 &&
        responseDrafts.every((draft) => draft && draft.ready);
      const hasShownInitialStatus = messages.some(
        item =>
          item.role === "assistant" &&
          Array.isArray(item.drafts) &&
          item.drafts.length
      );
      patch({
        busy: false,
        phase: draftsReady ? "ready" : "needs_input",
        language: replyLanguage,
        sessionId: data.sessionId || voiceAdd.sessionId || null,
        drafts: responseDrafts,
        readyForReview: draftsReady,
        messages: messages.concat(
          {
            role: "user",
            text: message,
            language: turnLanguage,
          },
          {
            role: "assistant",
            text: reply,
            language: replyLanguage,
            drafts: responseDrafts,
            showStatus: !hasShownInitialStatus && responseDrafts.length > 0,
            isCompletion: draftsReady,
          }
        ),
      });
    } catch (error) {
      patch({
        busy: false,
        phase: "partial_failure",
        messages: messages.concat(
          {
            role: "user",
            text: message,
            language: turnLanguage,
          },
          {
            role: "assistant",
            text: errorCopy[turnLanguage] || errorCopy.English,
            language: turnLanguage,
          }
        ),
      });
    }
  };

  sendRef.current = send;

  const listen = async () => {
    if (wantMic.current) {
      stopListening();
      return;
    }
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    const unsupported = micErrorCopy.unsupported;
    if (!Speech || !navigator.mediaDevices) {
      setMicNote(unsupported[language] || unsupported.English);
      return;
    }
    if (busy) return;
    wantMic.current = true;
    patch({ phase: "listening" });
    setMicNote("");
    setListening(true);
    spokenRef.current = textRef.current.trim();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      wantMic.current = false;
      setListening(false);
      const denied = error && (error.name === "NotAllowedError" || error.name === "SecurityError");
      const copy = micErrorCopy[denied ? "not-allowed" : "audio-capture"];
      setMicNote(copy[language] || copy.English);
      return;
    }
    if (!wantMic.current) return;
    startRecognition();
  };

  const chooseSpeech = (next) => {
    setSpeechLang(next);
    speechLangRef.current = next;
    if (wantMic.current) startRecognition();
  };

  const startScan = (draft, scan) => {
    dispatch(setVoiceAdd({ scan: { draftId: draft.id, key: scan.key, label: scan.label } }));
    dispatch(setMembershipCardsScannerPopupOpen(true, "voiceAdd"));
  };

  const editDraft = async (draft) => {
    const launchId = ++editLaunchRef.current;
    patch({ phase: "editing", editingDraftId: draft.id, open: true });
    const opened = await openDraftForm(draft, {
      openClass,
      openWorkshop,
      openCourt,
      onStaffPermissions,
    });
    trackVoiceAddEvent("edit", { draftId: draft.id, type: draft.type, label: draft.label });
    if (!opened && launchId === editLaunchRef.current) {
      patch({
        phase: "ready",
        step: "review",
        prefill: null,
        editingDraftId: null,
      });
    }
  };

  const memberProfilePath = (memberId) => {
    if (!memberId) return "";
    const login = JSON.parse(
      localStorage.getItem("connectToFitGymPanelUser") || "{}"
    );
    return login.role === "TRAINER"
      ? `/trainer-panel/membersinfo/${memberId}`
      : `/gym-panel/membersinfo/${memberId}`;
  };

  const applyExistingMember = async (draft, member, continueConfirm) => {
    const selected = memberRecord(member);
    if (!draft || !selected) return;
    const nextDrafts = attachExistingMember(drafts, draft.id, selected);
    const used = {
      outcome: "used",
      draft: nextDrafts.find((item) => item.id === draft.id) || draft,
      createdId: selected.id,
      createdName: selected.name,
      message: "Using the selected member.",
      openLabel: "Open member",
      openPath: memberProfilePath(selected.id),
    };
    const nextResults = (results || [])
      .filter((item) => item.outcome !== "blocked")
      .map((item) => (item.draft.id === draft.id ? { ...item, ...used } : item));
    if (!nextResults.some((item) => item.draft.id === draft.id)) {
      nextResults.push(used);
    }
    trackVoiceAddEvent("use_existing", {
      draftId: draft.id,
      memberId: selected.id,
      memberName: selected.name,
    });
    setPickingDraftId(null);
    patch({
      drafts: nextDrafts,
      results: nextResults,
      readyForReview:
        nextDrafts.length > 0 && nextDrafts.every((item) => item.ready),
    });
    if (continueConfirm) {
      await confirmAll(nextDrafts, nextResults);
    }
  };

  const confirmAll = async (sourceDrafts, sourceResults) => {
    const activeDrafts = Array.isArray(sourceDrafts) ? sourceDrafts : drafts;
    const activeResults = Array.isArray(sourceResults) ? sourceResults : results;
    if (busy || phase === "editing" || !activeDrafts.length) return;
    setProgress({ current: 0, total: activeDrafts.length, label: "Preparing…" });
    patch({ busy: true, phase: "confirming" });
    const next = [];
    const created = {};
    const completed = activeResults.reduce((index, item) => {
      if (item.outcome === "created" || item.outcome === "used") {
        index[item.draft.id] = item;
      }
      return index;
    }, {});
    let ordered;
    try {
      ordered = dependencyOrder(activeDrafts);
    } catch (error) {
      setProgress(null);
      const blocked = activeDrafts.map((draft) => ({
        outcome: "blocked",
        draft,
        message:
          "These adds depend on each other. Go back to Review, reject one of them, then Confirm All again.",
      }));
      trackVoiceAddConfirm(blocked);
      patch({
        busy: false,
        phase: "partial_failure",
        step: "results",
        results: blocked,
      });
      return;
    }
    try {
      for (const draft of ordered) {
        setProgress({
          current: next.length + 1,
          total: ordered.length,
          label: draft.label,
        });
        const previous = completed[draft.id];
        if (previous) {
          if (previous.createdId) {
            created[draft.id] = {
              id: previous.createdId,
              name: previous.createdName,
            };
          }
          next.push(previous);
          continue;
        }
        const linked = applyCreatedDependencies(draft, created);
        const unresolved = pendingDependencies(linked);
        const waitingOn = unresolved
          .map((id) => {
            const found = activeDrafts.find((item) => item.id === id);
            return found && found.label;
          })
          .filter(Boolean);
        const outcome = unresolved.length
          ? {
              outcome: "blocked",
              draft: linked,
              waitingOnIds: unresolved,
              message: waitingOn.length
                ? `Waiting on ${waitingOn.join(
                    ", "
                  )}. Fix that add, then Confirm All again.`
                : "Waiting on another add. Fix the missing add, then Confirm All again.",
            }
          : await confirmDraft(linked);
        if (
          (outcome.outcome === "created" || outcome.outcome === "used") &&
          outcome.createdId
        ) {
          created[draft.id] = {
            id: outcome.createdId,
            name: outcome.createdName,
          };
        }
        next.push({ ...outcome, draft: linked });
        if (FAILED_OUTCOMES.includes(outcome.outcome)) {
          ordered.slice(next.length).forEach((left) => {
            next.push({
              outcome: "blocked",
              draft: applyCreatedDependencies(left, created),
              waitingOnIds: [linked.id],
              message: `${linked.label} did not complete. Fix that, then Confirm All again.`,
            });
          });
          break;
        }
      }
      const failed = next.some((item) => FAILED_OUTCOMES.includes(item.outcome));
      trackVoiceAddConfirm(next);
      patch({
        busy: false,
        phase: failed ? "partial_failure" : "complete",
        step: "results",
        results: next,
        editingDraftId: null,
        drafts: activeDrafts.map((draft) => {
          const result = next.find((item) => item.draft.id === draft.id);
          if (
            !result ||
            result.outcome === "created" ||
            result.outcome === "used"
          ) {
            return { ...draft, fieldErrors: [] };
          }
          return {
            ...draft,
            fieldErrors: result.problems || result.draft.fieldErrors || [],
          };
        }),
      });
    } finally {
      setProgress(null);
    }
  };

  const rejectDraft = (draft) => {
    const remaining = drafts.filter((item) => item.id !== draft.id);
    const nextResults = results.concat({ outcome: "rejected", draft });
    trackVoiceAddEvent("reject", { draftId: draft.id, type: draft.type, label: draft.label });
    patch({
      drafts: remaining,
      results: nextResults,
      readyForReview: remaining.length > 0 && remaining.every((item) => item.ready),
      step: remaining.length ? "review" : "results",
    });
  };

  const resetComposer = () => {
    clearTimeout(editTimer.current);
    editingRef.current = false;
    spokenRef.current = "";
    setMicNote("");
    patch({ text: "" });
  };

  const editText = (value) => {
    spokenRef.current = value;
    patch({ text: value });
    if (!wantMic.current) return;
    editingRef.current = true;
    clearTimeout(editTimer.current);
    editTimer.current = setTimeout(() => {
      editingRef.current = false;
      if (wantMic.current) startRecognition();
    }, 450);
  };

  const resetPanelOffset = () => {
    panelOffsetRef.current = { x: 0, y: 0 };
    setPanelOffset({ x: 0, y: 0 });
  };

  const hidePopup = () => {
    stopListening();
    resetPanelOffset();
    patch({ open: false });
  };

  const startOver = (keepOpen) => {
    editLaunchRef.current += 1;
    cancelPendingDraftForm();
    stopListening();
    resetComposer();
    endVoiceAddSession("discard");
    patch({
      ...voiceAddInitialState,
      open: Boolean(keepOpen),
    });
  };

  const finishSession = () => {
    editLaunchRef.current += 1;
    cancelPendingDraftForm();
    stopListening();
    resetComposer();
    endVoiceAddSession("close");
    patch({ ...voiceAddInitialState, open: false });
  };

  const openResultRecord = (item) => {
    const path = item && item.openPath;
    if (path) window.open(path, "_blank", "noopener,noreferrer");
  };

  const discard = () => startOver(true);

  const locked =
    busy || phase === "confirming" || Boolean(progress);
  const showWorkOverlay =
    (step === "review" || step === "results") && locked;
  const overlayTitle =
    progress && progress.label && progress.label !== "Preparing…"
      ? `Creating ${progress.label}`
      : "Creating your adds";
  const overlayDetail = progress
    ? `${Math.max(progress.current, 1)} of ${progress.total}`
    : "Please wait. This can take a few seconds.";

  const stepItems = [
    { id: "chat", label: "Chat", enabled: true },
    { id: "review", label: "Review", enabled: drafts.length > 0 },
    { id: "results", label: "Results", enabled: results.length > 0 },
  ];
  const currentStepIndex = Math.max(
    0,
    stepItems.findIndex((item) => item.id === step)
  );
  const previousStep = currentStepIndex > 0 ? stepItems[currentStepIndex - 1] : null;
  const goToStep = (id) => {
    if (id === "chat") patch({ step: "chat" });
    if (id === "review" && drafts.length) patch({ step: "review", phase: "ready" });
    if (id === "results" && results.length) patch({ step: "results" });
  };

  if (!panelShown || typeof document === "undefined") return null;

  const vp = typeof window === "undefined" ? { width: 0, height: 0 } : viewportSize();
  const fabEl =
    typeof document === "undefined"
      ? null
      : document.querySelector(".voice-add-fab");
  const mic =
    typeof window === "undefined"
      ? { x: 0, y: 0, left: 0 }
      : micCenter(fabRaised, fabEl);
  const connector =
    typeof window === "undefined"
      ? ""
      : arrowPathToMic(panelSize, panelOffset, mic);

  return createPortal(
    <>
      <svg
        className={`voice-add-dock__tail${
          panelReady && !panelLeaving ? " is-open" : ""
        }`}
        viewBox={`0 0 ${vp.width} ${vp.height}`}
        width={vp.width}
        height={vp.height}
        aria-hidden="true"
        focusable="false"
      >
        <path d={connector} />
      </svg>
      <div
        className={`voice-add-dock${
          panelReady && !panelLeaving ? " is-open" : ""
        }${panelLeaving ? " is-leaving" : ""}${
          panelResizing || panelDragging ? " is-resizing" : ""
        }${panelDragging ? " is-dragging" : ""}${fabRaised ? " is-raised" : ""}`}
        style={{
          width: panelSize.width,
          height: panelSize.height,
          top: PANEL_GUTTER + panelOffset.y,
          right: PANEL_MIC_SLOT - panelOffset.x,
        }}
      >
      <div
        ref={panelRef}
        className="voice-add-panel"
        role="dialog"
        aria-label="Add with Voice"
        aria-modal="false"
      >
      <div
        className="voice-add-panel__drag-edge is-n"
        aria-hidden="true"
        onMouseDown={beginPanelDrag}
      />
      <div
        className="voice-add-panel__drag-edge is-e"
        aria-hidden="true"
        onMouseDown={beginPanelDrag}
      />
      <div
        className="voice-add-panel__drag-edge is-s"
        aria-hidden="true"
        onMouseDown={beginPanelDrag}
      />
      <div
        className="voice-add-panel__drag-edge is-w"
        aria-hidden="true"
        onMouseDown={beginPanelDrag}
      />
      <div
        className="voice-add-panel__resize is-s"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize height"
        onMouseDown={(event) => beginPanelResize(event, "s")}
      />
      <div
        className="voice-add-panel__resize is-w"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize width"
        onMouseDown={(event) => beginPanelResize(event, "w")}
      />
      <div
        className="voice-add-panel__resize is-sw"
        role="separator"
        aria-label="Resize"
        onMouseDown={(event) => beginPanelResize(event, "sw")}
      />
      <div className="voice-add" data-phase={phase}>
        <div className="voice-add__head" onMouseDown={beginPanelDrag}>
          <span className="voice-add__drag" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <div className="voice-add__brand">
            <span className="voice-add__mark" aria-hidden="true">
              <MicIcon />
            </span>
            <div>
              <h2 className="voice-add__title">Add with Voice</h2>
              <p className="voice-add__subtitle">Nothing is saved until you confirm.</p>
            </div>
          </div>
          <div className="voice-add__head-actions">
            <button
              type="button"
              className="voice-add__reset"
              onClick={() => startOver(true)}
            >
              Start over
            </button>
            <button
              type="button"
              className="voice-add__close"
              onClick={hidePopup}
              aria-label="Hide"
            >
              ×
            </button>
          </div>
        </div>
        <nav
          className="voice-add__steps"
          aria-label="Voice add steps"
          onMouseDown={(event) => {
            if (event.target.closest && event.target.closest("button")) return;
            beginPanelDrag(event);
          }}
        >
          {previousStep ? (
            <button
              type="button"
              className="voice-add__back"
              disabled={locked || !previousStep.enabled}
              onClick={() => goToStep(previousStep.id)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15.5 5.5 8 12l7.5 6.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to {previousStep.label}
            </button>
          ) : null}
          <div className="voice-add__step-list">
          {stepItems.map((item, index) => {
            const current = step === item.id;
            const done =
              (item.id === "chat" && step !== "chat") ||
              (item.id === "review" && step === "results");
            return (
              <React.Fragment key={item.id}>
                {index > 0 ? (
                  <span
                    className={`voice-add__rail${
                      done || current ? " is-on" : ""
                    }`}
                    aria-hidden="true"
                  />
                ) : null}
                <button
                  type="button"
                  className={`voice-add__step${current ? " is-on" : ""}${
                    done ? " is-done" : ""
                  }`}
                  aria-label={item.label}
                  aria-current={current ? "step" : undefined}
                  disabled={locked || !item.enabled}
                  onClick={() => goToStep(item.id)}
                >
                  <em aria-hidden="true">{done ? "✓" : index + 1}</em>
                  <span>{item.label}</span>
                </button>
              </React.Fragment>
            );
          })}
          </div>
        </nav>

        {step === "chat" && (
          <>
            <div className="voice-add__chat" ref={chatRef}>
              {!messages.length && !busy && (
                <div className="voice-add__empty">
                  <button
                    type="button"
                    className={`voice-add__empty-mic${listening ? " is-on" : ""}`}
                    aria-label={listening ? "Stop recording" : "Start recording"}
                    aria-pressed={listening}
                    onClick={listen}
                  >
                    <span className="voice-add__empty-mic-ring is-3" aria-hidden="true" />
                    <span className="voice-add__empty-mic-ring is-2" aria-hidden="true" />
                    <span className="voice-add__empty-mic-ring is-1" aria-hidden="true" />
                    <span className="voice-add__empty-mic-btn">
                      {listening ? <PauseIcon /> : <MicIcon />}
                    </span>
                  </button>
                  <b>Say what you want to add</b>
                  <p>Tap the mic, pick EN or عربي, then speak. You can delete words before sending.</p>
                </div>
              )}
              {messages.map((message, index) => (
                <React.Fragment key={`${message.role}-${index}`}>
                  {message.isCompletion ? (
                    <StatusTable
                      drafts={message.drafts}
                      language={message.language}
                    />
                  ) : null}
                  <ChatMessage message={message} />
                  {message.showStatus && !message.isCompletion ? (
                    <StatusTable
                      drafts={message.drafts}
                      language={message.language}
                    />
                  ) : null}
                </React.Fragment>
              ))}
              {busy && (
                <div
                  className={`voice-add__typing${
                    language === "Arabic" ? " is-ar" : ""
                  }`}
                  role="status"
                >
                  <span className="voice-add__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="voice-add__typing-copy">
                    <b>
                      {(workingCopy[language] || workingCopy.English).title}
                    </b>
                    <small>
                      {(workingCopy[language] || workingCopy.English).detail}
                    </small>
                  </span>
                </div>
              )}
            </div>
            {listening && (
              <div className="voice-add__listen">
                <span className="voice-add__bars" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
                <span>{listeningCopy[speechLang]}</span>
              </div>
            )}
            {micNote && !listening && (
              <p className="voice-add__mic-note">{micNote}</p>
            )}
            <form
              className="voice-add__composer"
              onSubmit={(event) => {
                event.preventDefault();
                send(text);
              }}
            >
              <div className="voice-add__field">
                <textarea
                  ref={inputRef}
                  className="voice-add__input"
                  rows={2}
                  value={text}
                  placeholder="Say what to add, or type it"
                  onChange={(event) => editText(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                  onCopy={(event) => event.stopPropagation()}
                  onCut={(event) => event.stopPropagation()}
                  onPaste={(event) => event.stopPropagation()}
                />
                {text ? (
                  <button
                    type="button"
                    className="voice-add__clear"
                    onClick={() => editText("")}
                    aria-label="Clear text"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              <div className="voice-add__hear" role="group" aria-label="Microphone language">
                <button
                  type="button"
                  className={speechLang === "en-US" ? "is-on" : ""}
                  onClick={() => chooseSpeech("en-US")}
                >
                  EN
                </button>
                <button
                  type="button"
                  className={speechLang === "ar-SA" ? "is-on" : ""}
                  onClick={() => chooseSpeech("ar-SA")}
                >
                  عربي
                </button>
              </div>
              <button
                type="button"
                className={`voice-add__mic${listening ? " is-on" : ""}`}
                onClick={listen}
                aria-pressed={listening}
                aria-label={listening ? "Pause" : "Mic"}
              >
                {listening ? <PauseIcon /> : <MicIcon />}
                {listening ? "Pause" : null}
              </button>
              <button className="voice-add__send" type="submit" disabled={busy || !text.trim()}>
                {busy ? "ANALYZING" : "SEND"}
              </button>
              {drafts.length > 0 && (
                <button
                  className="voice-add__primary"
                  type="button"
                  onClick={() => patch({ step: "review", phase: "ready" })}
                >
                  REVIEW
                </button>
              )}
            </form>
          </>
        )}

        {step === "review" && (
          <div className="voice-add__board">
            <div className="voice-add__board-head">
              <div>
                <h3>Review before creating</h3>
                <p>
                  Check each add, fill anything missing, then confirm. Nothing
                  is saved until you confirm.
                </p>
              </div>
              <div className="voice-add__counts" aria-label="Review status">
                <span className="is-ok">
                  {
                    drafts.filter(
                      (draft) => draft.ready && !issueList(draft).length
                    ).length
                  }{" "}
                  ready
                </span>
                {drafts.some(
                  (draft) => !draft.ready || issueList(draft).length
                ) ? (
                  <span className="is-bad">
                    {
                      drafts.filter(
                        (draft) => !draft.ready || issueList(draft).length
                      ).length
                    }{" "}
                    need attention
                  </span>
                ) : null}
              </div>
            </div>
            <div className="voice-add__cards">
              {drafts.map((draft) => {
                const issues = issueList(draft);
                const ready = Boolean(draft.ready) && issues.length === 0;
                return (
                  <article
                    className={`voice-add__card${ready ? "" : " is-issue"}`}
                    key={draft.id}
                  >
                    <header className="voice-add__card-head">
                      <div>
                        <b>{draft.label}</b>
                        <span className="voice-add__card-meta">
                          {(draft.saved || []).length} detail
                          {(draft.saved || []).length === 1 ? "" : "s"} captured
                        </span>
                      </div>
                      <StatusBadge tone={ready ? "ok" : "bad"}>
                        {ready
                          ? "Ready"
                          : (draft.missing || []).length
                          ? `${draft.missing.length} missing`
                          : "Needs attention"}
                      </StatusBadge>
                    </header>
                    <FieldList
                      items={draft.saved}
                      empty="No details captured yet. Use Edit to fill this add."
                    />
                    <IssueList
                      issues={issues}
                      title={
                        issues.length > 1
                          ? "Fix these before confirming"
                          : "Fix this before confirming"
                      }
                    />
                    <div className="voice-add__card-actions">
                      {(draft.scans || []).map((scan) => (
                        <button
                          type="button"
                          className="voice-add__btn"
                          key={scan.key}
                          disabled={locked}
                          onClick={() => startScan(draft, scan)}
                        >
                          Scan {scan.label}
                        </button>
                      ))}
                      {(draft.type === "member" || draft.type === "sale") && (
                        <button
                          type="button"
                          className="voice-add__btn"
                          disabled={locked}
                          onClick={() =>
                            setPickingDraftId(
                              pickingDraftId === draft.id ? null : draft.id
                            )
                          }
                        >
                          Select member
                        </button>
                      )}
                      <button
                        type="button"
                        className="voice-add__btn"
                        disabled={locked}
                        onClick={editDraft.bind(null, draft)}
                      >
                        {phase === "editing" &&
                        voiceAdd.editingDraftId === draft.id
                          ? "Opening…"
                          : "Edit"}
                      </button>
                      <button
                        type="button"
                        className="voice-add__btn is-danger"
                        disabled={locked}
                        onClick={() => rejectDraft(draft)}
                      >
                        Reject
                      </button>
                    </div>
                    {pickingDraftId === draft.id ? (
                      <MemberSearch
                        onSelect={(member) =>
                          applyExistingMember(draft, member, false)
                        }
                        onCancel={() => setPickingDraftId(null)}
                      />
                    ) : null}
                  </article>
                );
              })}
            </div>
            <div className="voice-add__foot">
              <button
                type="button"
                className="voice-add__ghost"
                disabled={locked}
                onClick={discard}
              >
                Discard
              </button>
              <button
                type="button"
                className="voice-add__primary"
                disabled={locked || !drafts.length}
                onClick={confirmAll}
              >
                {busy || phase === "confirming" ? (
                  <>
                    <span className="voice-add__spinner is-btn" />
                    CREATING…
                  </>
                ) : (
                  "CONFIRM ALL"
                )}
              </button>
            </div>
          </div>
        )}

        {step === "results" && (
          <div className="voice-add__board">
            {(() => {
              const counts = resultCounts(results);
              const failed = counts.attention > 0;
              return (
                <div
                  className={`voice-add__summary ${failed ? "is-warn" : "is-ok"}`}
                >
                  <b>
                    {counts.created} created
                    {counts.existing
                      ? ` · ${counts.existing} already existed`
                      : ""}
                    {counts.rejected ? ` · ${counts.rejected} rejected` : ""}
                    {counts.attention
                      ? ` · ${counts.attention} need a fix`
                      : ""}
                  </b>
                  <p>
                    {failed
                      ? "Nothing is stuck. Fix the items below or go back to Review, then try again."
                      : "All selected adds are done. You can close this window."}
                  </p>
                </div>
              );
            })()}
            <div className="voice-add__cards">
              {results.map((item, index) => {
                const liveDraft =
                  drafts.find((draft) => draft.id === item.draft.id) ||
                  item.draft;
                const waitingOnIds =
                  item.waitingOnIds || pendingDependencies(item.draft);
                const meta = outcomeMeta[item.outcome] || {
                  label: item.outcome,
                  tone: "muted",
                };
                const failed = FAILED_OUTCOMES.includes(item.outcome);
                const foundIssues = failed
                  ? issueList(liveDraft, item.problems)
                  : [];
                const issues = failed
                  ? foundIssues.length
                    ? foundIssues
                    : item.message
                    ? [{ message: item.message }]
                    : []
                  : [];
                return (
                  <article
                    className={`voice-add__card${
                      failed ? " is-issue" : item.outcome === "rejected" ? " is-muted" : " is-ok"
                    }`}
                    key={`${item.draft.id}-${index}`}
                  >
                    <header className="voice-add__card-head">
                      <div>
                        <b>{item.draft.label}</b>
                        {item.createdName ? (
                          <span className="voice-add__card-meta">
                            {item.createdName}
                          </span>
                        ) : null}
                      </div>
                      <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
                    </header>
                    <FieldList items={liveDraft.saved} />
                    <IssueList
                      issues={issues}
                      title={failed ? "Why this did not complete" : null}
                    />
                    <div className="voice-add__card-actions">
                      {item.openPath &&
                        ["created", "existing", "used"].includes(
                          item.outcome
                        ) && (
                        <button
                          type="button"
                          className="voice-add__btn"
                          onClick={() => openResultRecord(item)}
                        >
                          {item.openLabel || "Open"}
                        </button>
                      )}
                      {item.outcome === "existing" && (
                        <>
                          {item.createdId ? (
                            <button
                              type="button"
                              className="voice-add__btn is-accent"
                              disabled={locked}
                              onClick={() =>
                                applyExistingMember(
                                  liveDraft,
                                  {
                                    id: item.createdId,
                                    name:
                                      item.createdName ||
                                      [
                                        liveDraft.fields &&
                                          liveDraft.fields.firstName,
                                        liveDraft.fields &&
                                          liveDraft.fields.lastName,
                                      ]
                                        .filter(Boolean)
                                        .join(" "),
                                    phone:
                                      liveDraft.fields && liveDraft.fields.phone,
                                    firstName:
                                      liveDraft.fields &&
                                      liveDraft.fields.firstName,
                                    lastName:
                                      liveDraft.fields &&
                                      liveDraft.fields.lastName,
                                  },
                                  true
                                )
                              }
                            >
                              Use this member
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="voice-add__btn is-accent"
                            disabled={locked}
                            onClick={() =>
                              setPickingDraftId(
                                pickingDraftId === liveDraft.id
                                  ? null
                                  : liveDraft.id
                              )
                            }
                          >
                            Select member
                          </button>
                          <button
                            type="button"
                            className="voice-add__btn"
                            disabled={locked}
                            onClick={() => editDraft(liveDraft)}
                          >
                            {phase === "editing" &&
                            voiceAdd.editingDraftId === liveDraft.id
                              ? "Opening…"
                              : "Edit"}
                          </button>
                        </>
                      )}
                      {["needs_form", "error"].includes(item.outcome) && (
                        <>
                          {(liveDraft.type === "member" ||
                            liveDraft.type === "sale") && (
                            <button
                              type="button"
                              className="voice-add__btn"
                              disabled={locked}
                              onClick={() =>
                                setPickingDraftId(
                                  pickingDraftId === liveDraft.id
                                    ? null
                                    : liveDraft.id
                                )
                              }
                            >
                              Select member
                            </button>
                          )}
                          <button
                            type="button"
                            className="voice-add__btn is-accent"
                            disabled={locked}
                            onClick={() => editDraft(liveDraft)}
                          >
                            {phase === "editing" &&
                            voiceAdd.editingDraftId === liveDraft.id
                              ? "Opening…"
                              : "Fix in form"}
                          </button>
                        </>
                      )}
                      {(item.outcome === "needs_scan" ||
                        (liveDraft.scans || []).length > 0) &&
                        item.outcome !== "created" &&
                        item.outcome !== "existing" &&
                        (liveDraft.scans || item.draft.scans || []).map(
                          (scan) => (
                            <button
                              type="button"
                              className="voice-add__btn is-accent"
                              key={scan.key}
                              disabled={locked}
                              onClick={() => startScan(liveDraft, scan)}
                            >
                              Scan {scan.label}
                            </button>
                          )
                        )}
                      {item.outcome === "blocked" &&
                        waitingOnIds.map((id) => {
                          const needed =
                            drafts.find((draft) => draft.id === id) ||
                            (
                              results.find(
                                (result) => result.draft.id === id
                              ) || {}
                            ).draft;
                          if (!needed) return null;
                          return (
                            <button
                              type="button"
                              className="voice-add__btn is-accent"
                              key={id}
                              disabled={locked}
                              onClick={() =>
                                (needed.scans || []).length
                                  ? startScan(needed, needed.scans[0])
                                  : editDraft(needed)
                              }
                            >
                              Fix {needed.label}
                            </button>
                          );
                        })}
                    </div>
                    {pickingDraftId === liveDraft.id ? (
                      <MemberSearch
                        onSelect={(member) =>
                          applyExistingMember(liveDraft, member, true)
                        }
                        onCancel={() => setPickingDraftId(null)}
                      />
                    ) : null}
                  </article>
                );
              })}
            </div>
            <div className="voice-add__foot">
              {drafts.length > 0 && (
                <button
                  type="button"
                  className="voice-add__ghost"
                  disabled={locked}
                  onClick={() => patch({ step: "review", phase: "ready" })}
                >
                  Back to Review
                </button>
              )}
              {results.some((item) => FAILED_OUTCOMES.includes(item.outcome)) ? (
                <>
                  <button
                    type="button"
                    className="voice-add__ghost"
                    disabled={locked}
                    onClick={finishSession}
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    className="voice-add__primary"
                    disabled={locked}
                    onClick={confirmAll}
                  >
                    {busy || phase === "confirming" ? (
                      <>
                        <span className="voice-add__spinner is-btn" />
                        CREATING…
                      </>
                    ) : (
                      "TRY AGAIN"
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="voice-add__primary"
                  onClick={finishSession}
                >
                  DONE
                </button>
              )}
            </div>
          </div>
        )}
        {showWorkOverlay ? (
          <WorkOverlay title={overlayTitle} detail={overlayDetail} />
        ) : null}
      </div>
      </div>
    </div>
    </>,
    document.body
  );
};

export default VoiceAdd;
